import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { KeepoLoader } from "../../components/KeepoLoader";
import { Box } from "../../components/Layout";
import { PageTitle } from "../../components/PageTitle";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import PlusIcon from "../../icons/Plus";
import { listLinks, ShortLink } from "../../services/links";
import { EmptyState } from "../Sites/components/EmptyState";
import { NewSiteCard } from "../Sites/styles";
import { LinkCard } from "./components/LinkCard";
import { PeriodFilter } from "./components/PeriodFilter";
import { CreateButton, Filters, List, LoadingMore, LoadingScreen, Note } from "./styles";

const prefix = "pages.links";

const PERIODS = ["all", "7", "30", "90"] as const;
type Period = (typeof PERIODS)[number];

// The text typed is looked for once the person stops typing
const SEARCH_DELAY_MS = 400;

/**
 * Keepo Links: the person's short links (kpo.bio/<path>), newest first. Looked for by a text, a period and a tag;
 * each one copied, opened to change, paused or deleted from its card. 30 at a time.
 */
const LinksPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState<Period>("all");
  const [tag, setTag] = useState<string>();
  // every tag of the person, for the tag menu (the API sends them with each page)
  const [allTags, setAllTags] = useState<string[]>([]);
  const [links, setLinks] = useState<ShortLink[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  // Back from making a link: a toast with its address (and "Copiar"), once (a reload doesn't bring it back)
  const told = useRef(false);
  useEffect(() => {
    const created = (location.state as { created?: string } | null)?.created;
    if (!created || told.current) return;
    told.current = true;
    navigate(".", { replace: true, state: null });
    toast.success(t(`${prefix}.created`), {
      description: created.replace(/^https?:\/\//, ""),
      action: {
        label: t(`${prefix}.copyShort`),
        onClick: () =>
          navigator.clipboard
            .writeText(created)
            .then(() => toast.info(t(`${prefix}.copiedToast`)))
            .catch(() => toast.error(t(`${prefix}.copyError`), { description: t(`${prefix}.tryAgain`) })),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const filters = useCallback(
    (nextPage: number) => ({
      q: query || undefined,
      from:
        period === "all"
          ? undefined
          : dayjs()
              .subtract(Number(period) - 1, "day")
              .format("YYYY-MM-DD"),
      tag,
      page: nextPage > 1 ? nextPage : undefined,
    }),
    [query, period, tag]
  );

  // Which list is on screen: a new filter (a new load) makes the pages still coming of the old one go to waste
  const listVersion = useRef(0);

  const load = useCallback(() => {
    let current = true;
    listVersion.current += 1;
    setLoading(true);
    setLoadingMore(false);
    setError(false);
    listLinks(filters(1))
      .then(list => {
        if (!current) return;
        setLinks(list.items);
        setTotal(list.total);
        setAllTags(list.tags ?? []);
        setPage(1);
      })
      .catch(() => current && setError(true))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [filters]);

  useEffect(load, [load]);

  const loadMore = useCallback(() => {
    const version = listVersion.current;
    setLoadingMore(true);
    listLinks(filters(page + 1))
      .then(list => {
        if (version !== listVersion.current) return;
        setLinks(shown => [...(shown ?? []), ...list.items.filter(item => !shown?.some(link => link.id === item.id))]);
        setTotal(list.total);
        setPage(page + 1);
      })
      .catch(() => version === listVersion.current && setError(true))
      .finally(() => version === listVersion.current && setLoadingMore(false));
  }, [filters, page]);

  // A link gone moves every later link one place up on the server: dropping only its card would make the next page
  // skip one. So the card goes at once, and the pages on screen are asked again (with the person's tags, which may
  // have lost one)
  const onDeleted = (id: string) => {
    const version = listVersion.current;
    setLinks(shown => shown?.filter(item => item.id !== id));
    setTotal(count => count - 1);
    Promise.all(Array.from({ length: page }, (_, index) => listLinks(filters(index + 1))))
      .then(pages => {
        if (version !== listVersion.current) return;
        const items = pages.flatMap(list => list.items);
        setLinks(items.filter((item, index) => items.findIndex(other => other.id === item.id) === index));
        setTotal(pages[pages.length - 1].total);
        setAllTags(pages[0].tags ?? []);
      })
      .catch(() => undefined); // the list as it is, without the card, is right enough until the next load
  };

  // More links as the list scrolls: the next page loads when its end shows (the button only where the browser can't
  // tell); at the last page nothing more loads, and nothing is said
  const hasMore = !!links && links.length < total && !loading && !hasError;
  const canWatchEnd = typeof window.IntersectionObserver === "function";
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = listEnd.current;
    if (!hasMore || loadingMore || !canWatchEnd || !end) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, canWatchEnd, loadMore]);

  const filtering = !!query || period !== "all" || !!tag;

  // First load: the shape of the whole screen (title, filters, "Criar link", cards), as Meus Keepos
  if (!links && loading && !hasError) {
    return (
      <Box>
        <LoadingScreen role="status" aria-label={t(`${prefix}.loading`)}>
          <Skeleton className="title" />
          <span className="filters">
            <Skeleton className="search" />
            <Skeleton className="period" />
          </span>
          <Skeleton className="create" />
          <Skeleton className="card" />
          <Skeleton className="card" />
          <Skeleton className="card" />
        </LoadingScreen>
      </Box>
    );
  }

  // No link yet (and nothing being looked for): only the invitation, as Meus Keepos
  if (links?.length === 0 && !filtering && !loading && !hasError) {
    return (
      <EmptyState
        title={t(`${prefix}.empty.title`)}
        description={t(`${prefix}.empty.description`)}
        action={t(`${prefix}.create`)}
        onCreate={() => navigate("/links/new")}
      />
    );
  }

  return (
    <Box>
      <PageTitle>{t(`${prefix}.title`)}</PageTitle>

      <Filters>
        <input
          type="search"
          className="search"
          aria-label={t(`${prefix}.search`)}
          placeholder={t(`${prefix}.searchPlaceholder`)}
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <PeriodFilter<Period>
          className="period"
          label={t(`${prefix}.period`)}
          value={period}
          options={PERIODS.map(option => ({ value: option, label: t(`${prefix}.periods.${option}`) }))}
          onChange={setPeriod}
        />
        {/* only when a link has a tag; the one picked on a card shows here too */}
        {allTags.length > 0 && (
          <PeriodFilter<string>
            className="tag"
            label={t(`${prefix}.tag`)}
            value={tag ?? ""}
            options={[
              { value: "", label: t(`${prefix}.allTags`) },
              ...[...new Set([...(tag ? [tag] : []), ...allTags])].map(name => ({ value: name, label: name })),
            ]}
            onChange={value => setTag(value || undefined)}
          />
        )}
      </Filters>

      {hasError && (
        <Note role="alert">
          {t(`${prefix}.loadError`)}{" "}
          <button type="button" onClick={load}>
            {t(`${prefix}.retry`)}
          </button>
        </Note>
      )}

      {links && (
        <List aria-busy={loading}>
          {/* First, so making a link never needs scrolling past a long list (as Meus Keepos) */}
          <NewSiteCard as={Link} to="/links/new">
            <span className="icon">
              <PlusIcon width={14} height={14} color="currentColor" />
            </span>
            {t(`${prefix}.create`)}
          </NewSiteCard>
          {links.length === 0 && !loading && !hasError && <Note>{t(`${prefix}.noResults`)}</Note>}
          {links.map(link => (
            <LinkCard
              key={link.id}
              link={link}
              onTag={setTag}
              onChange={changed => setLinks(shown => shown?.map(item => (item.id === changed.id ? changed : item)))}
              onDeleted={onDeleted}
            />
          ))}
        </List>
      )}

      <div ref={listEnd} />
      {loadingMore && (
        <LoadingMore role="status" aria-label={t(`${prefix}.loadingMore`)}>
          <KeepoLoader size={20} color="var(--color-neutral-white)" />
        </LoadingMore>
      )}
      {hasMore && !canWatchEnd && !loadingMore && (
        <CreateButton as="button" type="button" className="secondary" onClick={loadMore}>
          {t(`${prefix}.loadMore`)}
        </CreateButton>
      )}
    </Box>
  );
};

export default LinksPage;
